import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import JsonLd from '@/components/base/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleData } from '@/mocks/articles';
import { allVideos } from '@/mocks/videos';
import { siteInfo } from '@/mocks/siteInfo';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => (slug ? articleData[slug] : undefined), [slug]);

  const relatedVideosData = useMemo(() => {
    if (!article) return [];
    return article.relatedVideos.map((rv) => {
      const found = allVideos.find((v) => v.slug === rv.slug);
      return found || rv;
    });
  }, [article]);

  if (!article) {
    return <NotFound />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.thumbnail,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.slug}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={`91视频,${article.tags.join(',')},${article.category},${article.title},91${article.category}在线观看`}
        canonicalUrl={`${siteInfo.url}/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogImage={article.thumbnail}
        ogType="article"
      />
      <JsonLd data={articleSchema} />
      <JsonLd data={faqSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <article className="py-8 md:py-12">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    {article.category}
                  </Link>
                  {article.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium bg-background-200 text-foreground-600 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views} 次观看
                  </span>
                  <span className="flex items-center gap-1 text-accent-600">
                    <i className="ri-star-fill"></i>
                    {article.rating} 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.duration}
                  </span>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden mb-10 bg-background-200">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`${article.title} - 91视频免费在线观看`}
                  className="w-full aspect-video object-cover"
                />
              </div>

              <div className="prose-custom mb-10">
                <p className="text-base text-foreground-700 leading-relaxed mb-8 bg-background-100 rounded-lg p-5 border-l-4 border-primary-500">
                  <strong className="text-foreground-900">简介：</strong>{article.summary}
                </p>

                {article.content.map((section, idx) => (
                  <section key={idx} className="mb-8">
                    <h2 className="text-xl font-bold text-foreground-900 font-heading mb-3">
                      {section.heading}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {section.text}
                    </p>
                  </section>
                ))}
              </div>

              {article.faq.length > 0 && (
                <section className="mb-10 bg-background-100 rounded-xl p-6 md:p-8">
                  <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">
                    常见问题
                  </h2>
                  <div className="space-y-4">
                    {article.faq.map((faq, idx) => (
                      <details key={idx} className="group bg-background-50 rounded-lg p-4 cursor-pointer">
                        <summary className="text-sm font-medium text-foreground-800 list-none flex items-center justify-between">
                          {faq.question}
                          <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                        </summary>
                        <p className="text-sm text-foreground-600 mt-3 leading-relaxed pl-0">
                          {faq.answer}
                        </p>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {relatedVideosData.length > 0 && (
                <section className="mb-10">
                  <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedVideosData.map((rv, idx) => (
                      <Link
                        key={idx}
                        to={`/article/${rv.slug}`}
                        className="group flex gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors"
                      >
                        <div className="w-28 h-16 rounded-md overflow-hidden flex-shrink-0 bg-background-300">
                          <img
                            src={'thumbnail' in rv ? rv.thumbnail as string : ''}
                            alt={'title' in rv ? rv.title as string : ''}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                            {'title' in rv ? rv.title as string : ''}
                          </h3>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-foreground-400 bg-background-200 px-1.5 py-0.5 rounded">
                              {'category' in rv ? rv.category as string : ''}
                            </span>
                            <span className="text-xs text-foreground-400">
                              {'duration' in rv ? rv.duration as string : ''}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}